// Public discovery and registration. Screens and optional adapters load on intent.
(() => {
  if (window.AplovioApps) return;
  if (!customElements.get('aplovio-page-apps')) {
    customElements.define('aplovio-page-apps', class extends HTMLElement {
      connectedCallback() {
        if (this.abort) return;
        this.abort = new AbortController();
      }
      get layer() { return this.querySelector('aplovio-layer'); }
      disconnectedCallback() {
        this.abort?.abort();
        this.abort = null;
        document.dispatchEvent(new CustomEvent('aplovio:page-apps-disposed', {detail: {owner: this}}));
      }
    });
  }
  const assetUrls = JSON.parse(document.getElementById('AplovioAppAssets').textContent);
  const providers = new Map();
  const purchases = new Map();
  const revisions = new WeakMap();
  const instances = new WeakMap();
  const signatures = new WeakMap();
  const keyPattern = /^[a-z][a-z0-9.-]{1,79}$/;
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(`aplovio:apps-${name}`, {detail}));
  const ownerOf = element => {
    if (!(element instanceof Element)) return null;
    return element.closest('aplovio-product-view')?.owner || element.closest('aplovio-product, aplovio-cart, aplovio-page-apps') ||
      [...document.querySelectorAll('aplovio-product')].find(owner => owner.nativeForm?.contains(element)) ||
      document.querySelector('aplovio-page-apps');
  };
  function context(element) {
    const owner = ownerOf(element);
    if (!owner?.isConnected) return null;
    if (!instances.has(owner)) instances.set(owner, crypto.randomUUID());
    const product = owner.matches('aplovio-product');
    const cart = owner.matches('aplovio-cart');
    if (product && !owner.productReady) return null;
    return Object.freeze({
      owner, instanceId: instances.get(owner), revision: revisions.get(owner) || 0,
      kind: product ? 'product' : cart ? 'cart' : 'page',
      productGid: product ? owner.dataset.productGid : null,
      variantId: product ? owner.variant?.value || null : null,
      quantity: product ? Number(owner.nativeForm?.elements.namedItem('quantity')?.value || 1) : null,
      sellingPlanId: product ? owner.nativeForm?.elements.namedItem('selling_plan')?.value || null : null,
      cartRevision: cart ? owner.version || 0 : null,
      available: product ? !!owner.selectedAvailable(owner.variant?.selectedOptions[0]) : null,
      options: Object.freeze(product ? owner.controls.map(control => Object.freeze({
        position: Number(control.dataset.optionIndex) + 1,
        name: control.dataset.optionName, value: control.value,
        valueId: control.selectedOptions[0]?.dataset.optionValueId || null
      })) : []),
      locale: owner.dataset.language || document.documentElement.lang,
      country: owner.dataset.country || null,
      form: product ? owner.nativeForm : null
    });
  }
  function changed(event) {
    const owner = ownerOf(event.target);
    if (!owner) return;
    // Revision follows purchase context, not every rendering notification.
    const value = context(owner);
    const signature = value && JSON.stringify([value.productGid, value.variantId, value.available,
      value.options, value.locale, value.country, value.quantity, value.sellingPlanId, value.cartRevision]);
    if (signatures.get(owner) === signature) return;
    signatures.set(owner, signature);
    revisions.set(owner, (revisions.get(owner) || 0) + 1);
    emit('context', {owner, context: context(owner)});
  }
  function subscribe(element, listener) {
    if (typeof listener !== 'function') throw new TypeError('A context listener is required');
    let owner = ownerOf(element);
    const abort = new AbortController();
    document.addEventListener('aplovio:apps-context', event => {
      owner ||= ownerOf(element);
      if (event.detail.owner === owner) listener(event.detail.context);
    }, {signal: abort.signal});
    listener(context(element));
    return () => abort.abort();
  }
  function register(provider) {
    if (!provider || !keyPattern.test(provider.id) || provider.version !== '1.0' ||
        typeof provider.mount !== 'function' || !Array.isArray(provider.slots) ||
        provider.actions != null && (!Array.isArray(provider.actions) || provider.actions.some(action => !keyPattern.test(action)) || typeof provider.canHandleAction !== 'function') ||
        provider.getInitialTitle != null && typeof provider.getInitialTitle !== 'function' ||
        !provider.slots.length || provider.slots.some(slot => !['product', 'cart', 'page'].includes(slot))) {
      throw new TypeError('Unsupported Aplovio app provider');
    }
    if (providers.has(provider.id)) throw new Error(`Provider already registered: ${provider.id}`);
    const record = Object.freeze({...provider, slots: Object.freeze([...provider.slots]), ...(provider.actions ? {actions: Object.freeze([...new Set(provider.actions)])} : {})});
    providers.set(record.id, record);
    emit('providers', {id: record.id, registered: true});
    return () => {
      if (providers.get(record.id) !== record) return;
      providers.delete(record.id);
      emit('providers', {id: record.id, registered: false});
    };
  }
  function registerPurchase(participant) {
    if (!participant || !keyPattern.test(participant.id) || participant.version !== '1.0' ||
        !(participant.form instanceof HTMLFormElement) || typeof participant.prepare !== 'function' ||
        !Array.isArray(participant.fields) || participant.fields.some(field => !/^properties\[[^\[\]\r\n]{1,128}\]$/.test(field))) {
      throw new TypeError('Unsupported Aplovio purchase participant');
    }
    const owner = ownerOf(participant.form);
    if (!owner || owner.nativeForm !== participant.form) throw new Error('Purchase form has no Product owner');
    const list = purchases.get(participant.form) || new Map();
    if (list.has(participant.id)) throw new Error(`Purchase participant already registered: ${participant.id}`);
    const record = Object.freeze({...participant, fields: [...participant.fields], owner});
    list.set(record.id, record);
    purchases.set(participant.form, list);
    owner.notifyViews();
    return () => {
      if (list.get(record.id) !== record) return;
      list.delete(record.id);
      if (!list.size) purchases.delete(participant.form);
      owner.notifyViews();
    };
  }
  const load = async (name, style) => {
    if (style) await window.AplovioLazy.asset(assetUrls[style], 'style');
    await window.AplovioLazy.asset(assetUrls[name], 'script');
  };
  async function clearPageSourceLayer(value, trigger) {
    if (value.kind !== 'page') return trigger;
    const target = value.owner.layer?.dialog;
    const top = window.AplovioLayers?.top();
    if (!top || top === target) return trigger;
    if (!(trigger instanceof Element) || !top.contains(trigger)) throw new Error('Another layer is active');
    const closed = await new Promise(resolve => {
      const finish = result => {
        clearTimeout(timer);
        document.removeEventListener('aplovio:layer-close', onClose);
        resolve(result);
      };
      const onClose = event => { if (event.target === top) finish(true); };
      const timer = setTimeout(() => finish(false), 4000);
      document.addEventListener('aplovio:layer-close', onClose);
      window.AplovioLayers.close(top, 'app-flow');
    });
    if (!closed) throw new Error('Another layer is active');
    const restored = document.activeElement;
    return restored instanceof Element && restored.getClientRects().length ? restored : trigger;
  }
  function actionProvider(action, element, input) {
    const value = context(element);
    if (!value) return null;
    const eligible = [...providers.values()].filter(provider => {
      try {return provider.actions?.includes(action) && provider.slots.includes(value.kind) && (!provider.eligible || provider.eligible(value)) && provider.canHandleAction(action,value,input) === true;}
      catch {return false;}
    });
    // Two competing integrations require merchant configuration, not registration-order guessing.
    return eligible.length === 1 ? eligible[0] : null;
  }
  async function openAction({action, owner: element, trigger = document.activeElement, input, isCurrent} = {}) {
    const initial = context(element), provider = actionProvider(action,element,input);
    if (!initial || !provider || isCurrent && !isCurrent()) return {status:'cancelled',reason:'action_unavailable'};
    await load('aplovio-flow-host.js','aplovio-flow.css');
    const current = context(element);
    if (!current || current.instanceId !== initial.instanceId || current.revision !== initial.revision || actionProvider(action,element,input) !== provider || isCurrent && !isCurrent()) return {status:'cancelled',reason:'context_changed'};
    const activeTrigger = await clearPageSourceLayer(current, trigger);
    const latest = context(current.owner);
    if (!latest || latest.instanceId !== initial.instanceId || latest.revision !== initial.revision || actionProvider(action,element,input) !== provider || isCurrent && !isCurrent()) return {status:'cancelled',reason:'context_changed'};
    return window.AplovioFlowHost.open({provider,context:latest,trigger:activeTrigger,input});
  }
  async function open({provider: id, owner: element, trigger = document.activeElement, input} = {}) {
    const candidate = ownerOf(element);
    if (candidate?.matches('aplovio-cart') && !candidate.hasAttribute('data-cart-ready')) await window.AplovioCartEntry.load();
    const initial = context(element);
    const provider = providers.get(id);
    if (!initial || !provider || !provider.slots.includes(initial.kind) ||
        provider.eligible && !provider.eligible(initial)) throw new Error('App flow is unavailable in this context');
    await load('aplovio-flow-host.js', 'aplovio-flow.css');
    const current = context(initial.owner);
    if (!current || current.instanceId !== initial.instanceId || current.revision !== initial.revision ||
        providers.get(id) !== provider) return {status: 'cancelled', reason: 'context_changed'};
    const activeTrigger = await clearPageSourceLayer(current, trigger);
    const latest = context(initial.owner);
    if (!latest || latest.instanceId !== initial.instanceId || latest.revision !== initial.revision ||
        providers.get(id) !== provider) return {status: 'cancelled', reason: 'context_changed'};
    return window.AplovioFlowHost.open({provider, context: latest, trigger: activeTrigger, input});
  }
  const participants = (form, {validateRequired = false} = {}) => {
    const records = [...(purchases.get(form)?.values() || [])];
    if (validateRequired && required(form).some(id => !records.some(record => record.id === id))) {
      throw new Error('purchase_provider_missing');
    }
    return records;
  };
  const required = form => {
    const owner = ownerOf(form);
    if (!owner) return [];
    return [...new Set([form, ...owner.querySelectorAll('[data-aplovio-purchase-required]')]
      .flatMap(node => (node.dataset.aplovioPurchaseRequired || '').split(/\s+/)).filter(Boolean))];
  };
  const needsPreparation = form => participants(form).length > 0 || required(form).length > 0;
  window.AplovioApps = Object.freeze({
    version: '1.0', capabilities: Object.freeze(['hosted-flow', 'product-context', 'page-context', 'purchase-preparation', 'context-actions']),
    getContext: context, subscribe, register, registerPurchase, open,
    getActionProvider: actionProvider, openAction,
    getProvider: id => providers.get(id) || null,
    purchaseParticipants: participants,
    needsPurchasePreparation: needsPreparation,
    supportsAcceleratedCheckout: form => !required(form).length && !participants(form).some(record => record.required !== false),
    preparePurchase: async (product, options) => {
      if (!participants(product.nativeForm, {validateRequired: true}).length) return new FormData(product.nativeForm);
      await load('aplovio-app-purchase.js');
      return window.AplovioAppPurchase.prepare(product, options);
    },
    openOptions: async (owner, options) => {
      await load('aplovio-product-flow.js');
      return window.AplovioProductFlow.open(owner, options);
    },
    openSizeGuide: async (owner, view, trigger) => {
      await load('aplovio-sizeguide-adapter.js');
      return open({provider: 'aplovio.size-guide', owner, trigger, input: {view}});
    }
  });
  for (const type of ['aplovio:product-ready', 'aplovio:product-state', 'aplovio:cart-updated']) {
    document.addEventListener(type, changed);
  }
  document.addEventListener('aplovio:product-ready', event => {
    const owner = ownerOf(event.target);
    if (!owner) return;
    let signature = required(owner.nativeForm).join(' ');
    const observer = new MutationObserver(() => {
      const next = required(owner.nativeForm).join(' ');
      if (next !== signature) { signature = next; owner.notifyViews(); }
    });
    observer.observe(owner, {childList: true, subtree: true, attributes: true, attributeFilter: ['data-aplovio-purchase-required']});
    owner.abort.signal.addEventListener('abort', () => observer.disconnect(), {once: true});
  });
  const disposed = event => {
    const owner = event.detail?.owner;
    if (!owner) return;
    purchases.delete(owner.nativeForm);
    instances.delete(owner);
    emit('context', {owner, context: null});
  };
  document.addEventListener('aplovio:product-disposed', disposed);
  document.addEventListener('aplovio:cart-disposed', disposed);
  document.addEventListener('aplovio:page-apps-disposed', disposed);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-aplovio-app-open]');
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if (!providers.has(trigger.dataset.aplovioAppOpen) || !context(trigger)) return;
    event.preventDefault();
    open({provider: trigger.dataset.aplovioAppOpen, owner: trigger, trigger}).catch(() => {
      emit('error', {provider: trigger.dataset.aplovioAppOpen, code: 'open_failed'});
    });
  });
  emit('ready', {version: '1.0'});
})();
